import { useEffect, useRef, useState } from "react";
import qq from "fine-uploader/lib/core/s3.js";
import apiRequest from "utils/ApiRequest/apiRequest";
import getErrorNameFromUpdateMailDetailsRes from "../Helpers/getErrorNameFromUpdateMailDetailsRes";
import FixDomainModal from "../Modals/FixDomainModal";
import saveMailsToLocalStorage from "../Helpers/saveMailsToLocalStorage";
import useSizeLimitModal from "../Helpers/useSizeLimitModal";
import useResetUploader from "../Helpers/useResetUploader";
import { awaiter } from "../Helpers/helpers";
import { getLocalStorage } from "utils/globalLocalStorage";
import { getCloudflareCaptchaTokenAsync } from "components/Reusable/Form/helpers";
import { beforeUploadAction, checkUploadLimit } from "../Helpers/uploaderAPI";
import { DefaultContainerNoClose } from "components/Structure/ModalManager/DefaultContainer";
import { useContent } from "contexts/contentContext/contentContext";
import { useModal } from "contexts/modalContext/modalContext";
import { useUploader } from "contexts/uploaderContext/uploaderContext";
import { useToasts } from "react-toast-notifications";
import { useCheckSenderEmail } from "../UploadForms/UserEmailInput";
import { useUser } from "contexts/userContext/userContext";
import { IFormBody, IHandleSubmit } from "../models/models";

const useHandleSubmit = ({
  formBody,
  setBody,
  getFormValuesRef,
  recaptchaRef,
  setCurrentOption,
  currentOption,
}: IHandleSubmit) => {
  const {
    uploaderGetFiles,
    uplaoderStartUpload,
    getSize,
    getUploadId,
    getMailId,
    uploaderSetMeta,
    setUploading,
    getUploading,
    getSaved,
    getVerifiedEmail,
    setHideSubmit,
    getIsUppy,
    getExpireAfter,
  } = useUploader();

  const {
    getTextByKey,
    getHtmlByKey,
    getLanguageCulture,
    getBasicSettingsData,
  } = useContent();

  const { isLoggedIn } = useUser();

  const { setModal } = useModal();

  const { addToast } = useToasts();

  const retriesRef = useRef(0);

  const resetUploadAction = useResetUploader();

  const viaLink = getLocalStorage("sendViaLink") === "link";

  const { handleCheckIfEmailExist, handleConfirmLoggedInUser } =
    useCheckSenderEmail({ viaLink });

  const [enableSubmit, setEnableSubmit] = useState<boolean | number>(false);

  const [captchaRequired, setCaptchaRequired] = useState(false);

  // handle action in case error on some response from server
  const handleRetryUpload = async (
    formBody: IFormBody,
    errorMessage: string
  ) => {
    if (retriesRef.current > 2 || !formBody) {
      addToast(getTextByKey("uploading-error-title"), {
        appearance: "error",
      });
      await apiRequest(
        "Mails",
        `MailLogError?encryptedMailId=${getMailId()}&errorMessage=${errorMessage}`
      );
      resetUploadAction();
    } else {
      retriesRef.current++;
      submitFormAfterVerification(formBody);
    }
  };

  // handeling modal with invalid domains
  const handleFixDomainModal = async (data, formBody: IFormBody) => {
    setEnableSubmit(false);
    setModal({
      modal: FixDomainModal,
      container: DefaultContainerNoClose,
      props: {
        mails: data,
        formBody,
        setBody,
        setEnableSubmit,
        callback: submitFormAfterVerification,
      },
    });
  };

  // @ts-ignore
  const modalHandler = useSizeLimitModal({
    setEnableSubmit,
    body: {
      SendOwnerEmail: formBody.SendOwnerEmail,
      ShareToEmailSubjectTitle: formBody.ShareToEmailSubjectTitle,
    },
    handleRetryUpload,
  });

  const submitFormAfterVerification = async (formBody: IFormBody) => {
    // const recaptchaResponse =
    //   getBasicSettingsData()?.recaptchaType === "google"
    //     ? await recaptchaRef.current.executeAsync()
    //     : await getCloudflareCaptchaTokenAsync(recaptchaRef);

    // collection all data from form to send to server
    const sumData = {
      ...formBody,
      SendToEmail: viaLink ? [] : formBody.SendToEmail,
      EncryptedMailId: getMailId(),
    };

    const resUpdateMailDetails = await apiRequest(
      "Mails",
      "ReactUpdateMailDetails",
      { ...sumData },
      "POST"
    );
    if (resUpdateMailDetails.status === "success") {
      // keeping sendToEmail in local storage for incognito users
      saveMailsToLocalStorage(sumData.SendToEmail);
      // if mail already uploading - do nothing and return
      if (getUploading()) {
        setBody(sumData);
        addToast(getTextByKey("mail-details-updated"), {
          appearance: "success",
        });
        setTimeout(() => {
          setHideSubmit(true);
        });
        return { status: "success" };
      } else {
        await awaiter(1000);
        // @ts-ignore
        const res = await checkUploadLimit({
          totalSize: getSize(),
          uploadId: getUploadId(),
          modalHandler,
        });
        // res will come only in case size limit is not exceed
        if (res?.status === "success") {
          setUploading(true);
          setHideSubmit(true);

          const withWatermark =
            sumData.PaymentAmount && sumData.PaymentAmount > 0 ? true : false;

          await uploaderSetMeta({
            getUniqueId: qq.getUniqueId,
            addWaterMark: withWatermark,
            addVideoWatermark: withWatermark && sumData.DisplayVideo,
            filesOwnerEmail: sumData.SendOwnerEmail,
            callback: () => {
              addToast(getTextByKey("uploading-error-uuid"), {
                appearance: "error",
              });
            },
          });

          await awaiter(300);

          document.title = `${getHtmlByKey("uploading-title")} ${
            sumData.ShareToEmailSubjectTitle || ""
          }: 0%`;
          // doing request to server to set all settings relevant to this mail and get confirmation that uploading allowed
          // if yes it will start upload
          const totalSize = getIsUppy()
            ? getSize()
            : uploaderGetFiles().reduce((acc, file) => acc + file.size, 0);

          // @ts-ignore
          const submitRes = await beforeUploadAction({
            files: uploaderGetFiles(),
            uploadId: getUploadId(),
            lang: getLanguageCulture(),
            userMail: sumData.SendOwnerEmail,
            totalSize: totalSize,
            uplaoderStartUpload: uplaoderStartUpload,
            code: null,
            isSaved: getSaved(),
            expireAfter: getExpireAfter(),
          });
          if (submitRes?.status === "error") {
            handleRetryUpload(
              formBody,
              "ReactValidateUploadSizeHomePage error"
            );
          }
        } else {
          return res;
        }
      }
    }

    if (resUpdateMailDetails.status === "error") {
      const error = getErrorNameFromUpdateMailDetailsRes(resUpdateMailDetails);

      switch (error) {
        case "Error #4200":
        case "Blocked": {
          window.location.pathname = "/en/user-blocked";
          break;
        }

        case "timeout-or-duplicate":
        case "Oops, verification failed, please try again":
        case "CaptchaRequired": {
          if (
            getBasicSettingsData().recaptchaKeyReact ||
            getBasicSettingsData().turnstileKey
          ) {
            setCaptchaRequired(true);

            await awaiter(100);

            const recaptchaResponse =
              getBasicSettingsData().recaptchaType === "google"
                ? await recaptchaRef.current.executeAsync()
                : await getCloudflareCaptchaTokenAsync(recaptchaRef);

            if (recaptchaResponse) {
              submitFormAfterVerification({ ...formBody, recaptchaResponse });
            } else {
              return { status: "error", data: "CaptchaRequired" };
            }
          }
          break;
        }

        case "TooManyRecipients": {
          addToast(getTextByKey("too-many-recipients"), {
            appearance: "error",
          });
          break;
        }

        case "user-is-suspended": {
          addToast(getTextByKey("login-error-inactive-user"), {
            appearance: "error",
          });
          handleRetryUpload(
            null,
            "suspended user email " + formBody.SendOwnerEmail
          );
          break;
        }

        case "suspicious-body-text": {
          addToast(getTextByKey("suspicious-body-text"), {
            appearance: "error",
          });
          break;
        }

        default: {
          handleRetryUpload(formBody, "ReactUpdateMailDetails error");
          break;
        }
      }
    }
  };

  // handeling request to server to check if mail domain is valid
  const handleFixDomain = async (formBody: IFormBody) => {
    const mailsArray = formBody.SendToEmail;

    // if there is no mails - just submit form
    if (viaLink) {
      await submitFormAfterVerification(formBody);
    } else {
      const res = await apiRequest(
        "MailDomainFixes",
        "ClientMailDomainFixesNewHomePage",
        {
          emails: mailsArray,
        },
        "POST"
      );

      // if there is no data from server, its mean all domains are valid - just submit form
      if (res.data) {
        handleFixDomainModal(res.data, formBody);
      } else {
        await submitFormAfterVerification(formBody);
      }
    }
  };

  // saving body when changing between via link and via email
  const saveEnteredData = async (checkStatus?: boolean) => {
    const res = await getFormValuesRef.current();
    if (checkStatus && !res.succeed) {
      return false;
    } else {
      setBody((prev: IFormBody) => ({
        ...prev,
        ...res.values,
        SendToEmail: formBody.SendToEmail || res.values.SendToEmail,
      }));
      return true;
    }
  };

  const handleSubmit = async (formBody: IFormBody) => {
    const isSuccess = await saveEnteredData(true);

    if (!isSuccess) {
      return { status: "error" };
    }

    if (!viaLink && formBody.SendToEmail?.length === 0) {
      setCurrentOption("send-plane");
      return {
        status: "error",
      };
    }

    if (viaLink) {
      formBody.SendToEmail = [];
    }

    if (formBody.SendOwnerEmail !== getVerifiedEmail() || !getVerifiedEmail()) {
      if (currentOption !== "send-plane") {
        setCurrentOption("send-plane");
        return { status: "error" };
      }
      if (!isLoggedIn()) {
        const res = await handleCheckIfEmailExist({
          email: formBody.SendOwnerEmail,
        });
        if (res) {
          return await handleFixDomain(formBody);
        } else {
          return { status: "error", message: "confirmation-required" };
        }
      } else {
        const isConfirmed = handleConfirmLoggedInUser();
        if (isConfirmed) {
          return await handleFixDomain(formBody);
        } else {
          return { status: "error", message: "confirmation-required" };
        }
      }
    } else {
      return await handleFixDomain(formBody);
    }
  };

  // if size is 0 - hide submit button
  useEffect(() => {
    if (!getUploading()) setHideSubmit(!getSize());
  }, [getSize()]);

  useEffect(() => {
    if (getUploading()) {
      if (enableSubmit) {
        setEnableSubmit(enableSubmit === true ? 1 : enableSubmit + 1);
      } else {
        setEnableSubmit(true);
      }
    }
  }, [formBody.SendToEmail]);

  return { handleSubmit, enableSubmit, captchaRequired };
};

export default useHandleSubmit;
